/* ==========================================================================
   ECOMMERCE HELPERS & VARIANT MANAGEMENT
   ========================================================================== */

export const AVAILABLE_COLORS = [
  { id: 'azul', name: 'Azul', hex: '#1e40af', keywords: ['AZUL', 'AZULES', 'BLUE', 'CELESTE', 'MARINO'] },
  { id: 'negro', name: 'Negro', hex: '#0f172a', keywords: ['NEGRO', 'NEGRA', 'NEGRAS', 'NEGROS', 'BLACK'] },
  { id: 'rojo', name: 'Rojo', hex: '#dc2626', keywords: ['ROJO', 'ROJA', 'ROJAS', 'ROJOS', 'RED', 'BORDO'] },
  { id: 'verde', name: 'Verde', hex: '#16a34a', keywords: ['VERDE', 'VERDES', 'GREEN', 'LIMA', 'ESMERALDA'] },
  { id: 'amarillo', name: 'Amarillo', hex: '#eab308', keywords: ['AMARILLO', 'AMARILLA', 'AMARILLAS', 'AMARILLOS', 'YELLOW', 'DORADO'] },
  { id: 'blanco', name: 'Blanco', hex: '#ffffff', border: true, keywords: ['BLANCO', 'BLANCA', 'BLANCAS', 'BLANCOS', 'WHITE'] },
  { id: 'rosa', name: 'Rosa / Fucsia', hex: '#ec4899', keywords: ['ROSA', 'ROSAS', 'ROSADO', 'ROSADA', 'ROSADOS', 'ROSADAS', 'PINK', 'FUCSIA', 'MAGENTA', 'LILA', 'VIOLETA'] },
  { id: 'marron', name: 'Marrón / Kraft', hex: '#78350f', keywords: ['MARRON', 'MARRONES', 'BROWN', 'KRAFT'] },
  { id: 'transparente', name: 'Transparente', isTransparent: true, keywords: ['TRANSPARENTE', 'TRANSPARENTES', 'CRISTAL', 'INCOLORO'] },
  { id: 'surtido', name: 'Surtido / Multicolor', isRainbow: true, keywords: ['SURTIDO', 'SURTIDA', 'SURTIDOS', 'SURTIDAS', 'MULTICOLOR'] },
];

/**
 * Helper para verificar si un texto contiene una palabra clave de forma aislada
 */
const textContainsKeyword = (text, keyword) => {
  if (!text || !keyword) return false;
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(^|[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ])${escaped}([^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ]|$)`, 'i');
  return regex.test(text);
};

/**
 * Obtiene la lista de variantes/colores reales de un producto.
 */
export const getProductVariants = (product) => {
  if (!product) return [{ id: 'std', name: 'Estándar' }];

  if (product.variantes && Array.isArray(product.variantes) && product.variantes.length > 0) {
    return product.variantes;
  }

  // 0. Si el backend envió la lista oficial de colores desde mart060
  if (product.colores && Array.isArray(product.colores) && product.colores.length > 0) {
    return product.colores.map(c => {
      const cName = String(c.nombre || '').trim();
      const colorMatch = AVAILABLE_COLORS.find(ac => 
        ac.name.toUpperCase() === cName.toUpperCase() ||
        ac.keywords.some(kw => textContainsKeyword(cName, kw))
      );
      return {
        id: c.codigo || c.nombre,
        name: cName,
        color: colorMatch ? colorMatch.hex : null,
        isRainbow: colorMatch ? colorMatch.isRainbow : false,
        isTransparent: colorMatch ? colorMatch.isTransparent : false
      };
    });
  }

  // 1. Si es un producto con surtido / variedad multicolor en la descripción
  const desc = `${product.d_artic || ''} ${product.d_arti2 || ''}`.toUpperCase();
  const surtidoKeywords = ['SURTIDO', 'SURTIDA', 'SURTIDOS', 'SURTIDAS', 'MULTICOLOR'];
  const hasSurtido = surtidoKeywords.some(kw => textContainsKeyword(desc, kw));
  if (hasSurtido) {
    return [{ id: 'surtido', name: 'Surtido / Multicolor', isRainbow: true }];
  }

  // 2. Extraer si la descripción menciona algún color conocido
  const detectedColors = [];
  AVAILABLE_COLORS.forEach(color => {
    if (color.id !== 'surtido' && color.keywords.some(kw => textContainsKeyword(desc, kw))) {
      detectedColors.push({
        id: color.id,
        name: color.name,
        color: color.hex,
        isTransparent: color.isTransparent || false
      });
    }
  });

  if (detectedColors.length > 0) {
    return detectedColors;
  }

  return [{ id: 'std', name: 'Estándar' }];
};

/**
 * Verifica si un producto coincide con un color seleccionado en el filtro lateral.
 */
export const matchesProductColor = (product, selectedColorId) => {
  if (!selectedColorId) return true;
  const colorObj = AVAILABLE_COLORS.find(c => c.id === selectedColorId);
  if (!colorObj) return true;

  // 0. Si tiene colores oficiales del backend (Mart060)
  if (product.colores && Array.isArray(product.colores) && product.colores.length > 0) {
    return product.colores.some(c => {
      const cName = String(c.nombre || '').trim();
      return colorObj.keywords.some(kw => textContainsKeyword(cName, kw)) ||
             cName.toUpperCase() === colorObj.name.toUpperCase();
    });
  }

  // 1. Si tiene variantes explícitas configuradas
  if (product.variantes && Array.isArray(product.variantes) && product.variantes.length > 0) {
    return product.variantes.some(v => {
      if (v.id === selectedColorId) return true;
      if (v.name && colorObj.keywords.some(kw => textContainsKeyword(v.name, kw))) return true;
      return false;
    });
  }

  // 2. Si el usuario busca 'surtido' y el artículo tiene palabras clave de surtido
  const desc = `${product.d_artic || ''} ${product.d_arti2 || ''}`.toUpperCase();
  const surtidoKeywords = ['SURTIDO', 'SURTIDA', 'SURTIDOS', 'SURTIDAS', 'MULTICOLOR'];
  const hasSurtido = surtidoKeywords.some(kw => textContainsKeyword(desc, kw));
  if (selectedColorId === 'surtido' && hasSurtido) {
    return true;
  }

  // 3. Buscar en el texto de la descripción
  return colorObj.keywords.some(kw => textContainsKeyword(desc, kw));
};

/**
 * Convierte un color HEX (#RRGGBB o #RGB) a objeto { r, g, b }
 */
export const hexToRgb = (hex) => {
  if (!hex || typeof hex !== 'string') return null;
  let clean = hex.replace('#', '').trim();
  if (clean.length === 3) {
    clean = clean.split('').map(c => c + c).join('');
  }
  if (clean.length !== 6) return null;
  const num = parseInt(clean, 16);
  if (isNaN(num)) return null;
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255
  };
};

/**
 * Ajusta el brillo de un color HEX (porcentaje negativo para oscurecer, positivo para aclarar)
 */
export const adjustBrightness = (hex, percent) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const adjust = (val) => Math.max(0, Math.min(255, Math.round(val + (val * percent / 100))));
  const r = adjust(rgb.r).toString(16).padStart(2, '0');
  const g = adjust(rgb.g).toString(16).padStart(2, '0');
  const b = adjust(rgb.b).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
};

/**
 * Determina si un color de fondo es claro u oscuro para elegir texto blanco o negro contrastante
 */
export const getContrastTextColor = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#ffffff';
  // Fórmula de luminancia relativa estándar YIQ
  const yiq = ((rgb.r * 299) + (rgb.g * 587) + (rgb.b * 114)) / 1000;
  return (yiq >= 155) ? '#111827' : '#ffffff';
};

/**
 * Determina si una ruta corresponde a la Tienda o Página Web pública
 */
export const isWebOrStoreRoute = (pathname) => {
  const path = (pathname || (typeof window !== 'undefined' ? window.location.pathname : '')).toLowerCase();
  
  // Rutas explícitas del sistema ERP interno que NUNCA deben tener tema de tienda
  const systemPrefixes = [
    '/home',
    '/login',
    '/quoter',
    '/quote-viewer',
    '/addressbook',
    '/configuracion-tienda',
    '/configuracion-pagina',
    '/configuracion-web',
    '/admin',
    '/clientes-tienda',
    '/imagenes-articulos',
    '/roles-perfiles'
  ];

  if (systemPrefixes.some(prefix => path === prefix || path.startsWith(prefix + '/'))) {
    return false;
  }

  // Rutas públicas de la tienda y la landing web institucional
  const webPrefixes = [
    '/',
    '/home-web',
    '/web',
    '/tienda',
    '/shop',
    '/catalogo',
    '/novedades',
    '/descargas',
    '/listas-de-precios',
    '/catalogos',
    '/empresa',
    '/sobre-nosotros',
    '/nosotros',
    '/donde-estamos',
    '/ubicacion',
    '/contacto',
    '/contactenos',
    '/preguntas-frecuentes',
    '/faq',
    '/ayuda'
  ];

  return webPrefixes.some(prefix => path === prefix || path.startsWith(prefix + '/') || (prefix === '/' && path === ''));
};

/**
 * Restaura los estilos nativos de Bootstrap y del sistema ERP SISGECOM,
 * eliminando cualquier variable o estilo inyectado en :root y en el body.
 */
export const resetSystemTheme = () => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // 1. Eliminar variables de Bootstrap sobreescritas en :root
  root.style.removeProperty('--bs-primary');
  root.style.removeProperty('--bs-primary-rgb');
  root.style.removeProperty('--bs-primary-text-emphasis');
  root.style.removeProperty('--bs-primary-bg-subtle');
  root.style.removeProperty('--bs-primary-border-subtle');
  root.style.removeProperty('--bs-body-bg');
  root.style.removeProperty('--bs-body-color');
  root.style.removeProperty('--bs-border-color');
  root.style.removeProperty('--bs-link-color');
  root.style.removeProperty('--bs-link-color-rgb');
  root.style.removeProperty('--bs-link-hover-color');

  // 2. Limpiar estilos inline en el body del documento
  if (document.body) {
    document.body.style.backgroundColor = '';
    document.body.style.color = '';
  }
};

/**
 * Aplica la paleta de colores y estilos personalizada.
 * IMPORTANTE: Las variables nativas de Bootstrap y los estilos de body
 * SOLO se aplican si la ruta actual es una página pública (Tienda / Landing).
 * En rutas del sistema ERP, los colores del sistema jamás se tocan.
 */
export const applyEcommerceTheme = (colors, options = {}) => {
  if (!colors || typeof document === 'undefined') return;
  const root = document.documentElement;

  // Determinar si aplica al DOM global según la ruta activa
  const isWeb = isWebOrStoreRoute(options.pathname);
  const forceApply = options.force === true;

  const primary = colors.primaryColor || '#0d6efd';
  const secondary = colors.secondaryColor || adjustBrightness(primary, -15);
  const button = colors.buttonColor || colors.buttonPrimaryBg || '#ffc107';
  const buttonPrimaryText = colors.buttonPrimaryText || getContrastTextColor(button);
  const buttonSecondaryBg = colors.buttonSecondaryBg || '#f8fafc';
  const buttonSecondaryText = colors.buttonSecondaryText || '#1e293b';
  const accent = colors.accentColor || '#ff6b35';
  const textColor = colors.textColor || '#1e293b';
  const pageBg = colors.pageBg || '#f8fafc';
  const cardBg = colors.cardBg || colors.surfaceBg || '#ffffff';
  const borderColor = colors.borderColor || '#e2e8f0';
  const borderRadius = colors.borderRadius || '12px';

  // Cabecera & TopBar
  const topBarBg = colors.topBarBg || primary;
  const topBarText = colors.topBarText || getContrastTextColor(topBarBg);
  const navbarBg = colors.navbarBg || colors.headerBg || '#ffffff';
  const headerText = colors.headerText || getContrastTextColor(navbarBg);
  const headerBorder = colors.headerBorder || borderColor;

  // Footer
  const footerBg = colors.footerBg || '#1e293b';
  const footerTextColor = colors.footerTextColor || (getContrastTextColor(footerBg) === '#ffffff' ? '#94a3b8' : '#64748b');
  const footerTitleColor = colors.footerTitleColor || getContrastTextColor(footerBg);
  const footerBorderColor = colors.footerBorderColor || (getContrastTextColor(footerBg) === '#ffffff' ? '#334155' : '#e2e8f0');

  const sidebarBg = colors.sidebarBg || cardBg;

  const primaryRgb = hexToRgb(primary);
  const secondaryRgb = hexToRgb(secondary);
  const buttonRgb = hexToRgb(button);
  const accentRgb = hexToRgb(accent);
  const navbarRgb = hexToRgb(navbarBg);
  const footerBgRgb = hexToRgb(footerBg);

  const primaryDark = adjustBrightness(primary, -22);
  const primaryLight = primaryRgb ? `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.12)` : '#e7f1ff';
  const primaryLightBg = primaryRgb ? `rgba(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}, 0.06)` : '#f8fafc';
  const primaryText = getContrastTextColor(primary);

  // 1. Variables de Marca y Acentos (exclusivas de la tienda)
  root.style.setProperty('--ecom-primary', primary);
  if (primaryRgb) {
    root.style.setProperty('--ecom-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
  }
  root.style.setProperty('--ecom-primary-dark', primaryDark);
  root.style.setProperty('--ecom-primary-light', primaryLight);
  root.style.setProperty('--ecom-primary-light-bg', primaryLightBg);
  root.style.setProperty('--ecom-primary-text', primaryText);

  root.style.setProperty('--ecom-secondary', secondary);
  if (secondaryRgb) {
    root.style.setProperty('--ecom-secondary-rgb', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
  }

  root.style.setProperty('--ecom-accent', accent);
  root.style.setProperty('--ecom-accent-dark', adjustBrightness(accent, -18));
  root.style.setProperty('--ecom-accent-light', accentRgb ? `rgba(${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}, 0.14)` : '#fff3ed');

  // 2. Botones
  root.style.setProperty('--ecom-button', button);
  root.style.setProperty('--ecom-button-dark', adjustBrightness(button, -15));
  root.style.setProperty('--ecom-button-text', buttonPrimaryText);
  root.style.setProperty('--ecom-btn-secondary-bg', buttonSecondaryBg);
  root.style.setProperty('--ecom-btn-secondary-text', buttonSecondaryText);
  if (buttonRgb) {
    root.style.setProperty('--ecom-button-rgb', `${buttonRgb.r}, ${buttonRgb.g}, ${buttonRgb.b}`);
  }

  // 3. Fondos, Bordes y Textos Globales de Tienda
  root.style.setProperty('--ecom-bg', pageBg);
  root.style.setProperty('--ecom-card-bg', cardBg);
  root.style.setProperty('--ecom-surface-bg', cardBg);
  root.style.setProperty('--ecom-border', borderColor);
  root.style.setProperty('--ecom-radius', borderRadius);
  root.style.setProperty('--ecom-text', textColor);
  root.style.setProperty('--ecom-sidebar-bg', sidebarBg);
  root.style.setProperty('--ecom-sidebar-title', getContrastTextColor(sidebarBg));

  // 4. Cabecera (Header & TopBar)
  root.style.setProperty('--ecom-topbar-bg', topBarBg);
  root.style.setProperty('--ecom-topbar-text', topBarText);
  root.style.setProperty('--ecom-navbar-bg', navbarBg);
  root.style.setProperty('--ecom-navbar-text', headerText);
  root.style.setProperty('--ecom-header-border', headerBorder);
  if (navbarRgb) {
    root.style.setProperty('--ecom-navbar-rgb', `${navbarRgb.r}, ${navbarRgb.g}, ${navbarRgb.b}`);
  }

  // 5. Pie de Página (Footer)
  root.style.setProperty('--ecom-footer-bg', footerBg);
  root.style.setProperty('--ecom-footer-text', footerTextColor);
  root.style.setProperty('--ecom-footer-title', footerTitleColor);
  root.style.setProperty('--ecom-footer-border', footerBorderColor);
  if (footerBgRgb) {
    root.style.setProperty('--ecom-footer-bg-rgb', `${footerBgRgb.r}, ${footerBgRgb.g}, ${footerBgRgb.b}`);
  }

  // 6. Bootstrap CSS Custom Properties & Body Styling
  // SOLO y ÚNICAMENTE si estamos navegando en la Tienda o Página Web pública
  if (isWeb || forceApply) {
    if (primaryRgb) {
      root.style.setProperty('--bs-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
    }
    root.style.setProperty('--bs-primary', primary);
    root.style.setProperty('--bs-body-bg', pageBg);
    root.style.setProperty('--bs-body-color', textColor);
    root.style.setProperty('--bs-border-color', borderColor);

    if (typeof document !== 'undefined' && document.body) {
      document.body.style.backgroundColor = pageBg;
      document.body.style.color = textColor;
    }
  } else {
    // Si estamos en el sistema ERP, garantizamos que las variables nativas no se toquen
    resetSystemTheme();
  }
};

export const applyGlobalTheme = applyEcommerceTheme;
